import BaseProtable from "@/components/BaseProTable";
import { PrimaryButton } from "@/components/ui/buttons/PrimaryButton";
import CommonModal from "@/components/ui/CommonModal";
import { notifyWarning } from "@/utils/notification";
import { type TableProps } from "antd";
import { useState } from "react";
import { usePageTable } from "@/hooks/baseProTable/usePageTable";
import { getPurchaseRequestPage } from "@/apis/requirementManagement.api";
import {
  SrmPurchaseRequestListQueryDto,
  SrmPurchaseRequestVo,
} from "@/types/apis";

type SelectItem = SrmPurchaseRequestVo;

type Props = {
  open: boolean;
  onOk: (selectedRows: SelectItem[]) => void;
  onClose: () => void;
  /** 选择模式，默认多选 */
  selectionMode?: "multiple" | "single";
};

const PurchaseRequestRecordSelector: React.FC<Props> = ({
  open,
  onOk,
  onClose,
  selectionMode = "multiple",
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<SelectItem[]>([]);
  const [params, setParams] = useState<SrmPurchaseRequestListQueryDto>({});

  const { actionRef, fetchDatasource, columns, formRef } = usePageTable<
    SrmPurchaseRequestVo,
    SrmPurchaseRequestListQueryDto
  >({
    fetchFn: getPurchaseRequestPage,
    columns: [
      {
        title: "序号",
        valueType: "index",
        width: 60,
        align: "center",
        fixed: "left",
      },
      {
        title: "申请编号",
        dataIndex: "applicationNo",
        width: 150,
        align: "center",
      },
      {
        title: "物料名称",
        dataIndex: "materialNameCn",
        align: "center",
        render: (_, record) => {
          return record.materialNameCn || record.materialNameEn;
        },
      },
      {
        title: "搜索",
        dataIndex: "materialInfo",
        key: "materialInfo",
        hideInTable: true,
        fieldProps: {
          placeholder: "请输入物料编号/物料名称",
        },
        order: 99,
      },
      {
        title: "申请部门",
        dataIndex: "applyDepartment",
        align: "center",
        search: false,
      },
      {
        title: "申请日期",
        dataIndex: "createTime",
        align: "center",
        search: false,
      },
    ],
  });

  const rowSelection: TableProps<SelectItem>["rowSelection"] = {
    type: selectionMode === "single" ? "radio" : "checkbox",
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      if (selectionMode === "single") {
        setSelectedRowKeys(selectedRowKeys.slice(0, 1));
        setSelectedRows(selectedRows.slice(0, 1));
        return;
      }
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);
    },
  };

  const handleOk = () => {
    if (selectedRows.length === 0) {
      notifyWarning("请选择选择需求单");
      return;
    }
    onOk?.(selectedRows);
    handleClose();
  };

  const handleClose = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setParams({});
    onClose();
  };

  const footer = () => (
    <div style={{ textAlign: "center" }}>
      <PrimaryButton onClick={handleOk}>确定</PrimaryButton>
    </div>
  );

  return (
    <CommonModal
      title="选择需求单"
      open={open}
      onCancel={handleClose}
      width={1200}
      style={{ top: 20 }}
      footer={footer}
    >
      <BaseProtable<SelectItem>
        headerTitle={null}
        actionRef={actionRef}
        formRef={formRef}
        columns={columns}
        rowSelection={rowSelection}
        rowKey="id"
        bordered
        request={fetchDatasource}
        params={params}
        search={{
          labelWidth: "auto",
          span: 6,
        }}
        onSubmit={(values) => setParams({ reviewStatus: "2", ...values })}
      />
    </CommonModal>
  );
};

export default PurchaseRequestRecordSelector;
